const EM_DASH = "—";
const DAY_MS = 24 * 60 * 60 * 1000;

const pad2 = (n: number) => String(n).padStart(2, "0");

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Full-date formatter (YYYY-MM-DD) for the ops Groups "Added" columns, in the
 * VIEWER's local time. Unlike `fmtDate` (month + day only), this shows the
 * year. Null / empty / unparseable → em dash.
 *
 * IR-1425 r12b (Rotem 2026-10-12, comment 64191): "store and transfer UTC with
 * a zone marker, display in the user's local time with the zone visible" — on
 * every date column. These two Added columns are day-resolution facts, so
 * they switch to local getters but carry no zone label of their own; the zone
 * is a separate export (`viewerZoneLabel`) that the time-resolution cells
 * print beside the stamp, where a ±HH:MM actually changes the reading.
 */
export function fmtCreatedDate(iso: string | null): string {
  const d = parse(iso);
  if (!d) return EM_DASH;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * Date AND time (YYYY-MM-DD HH:MM) in the viewer's local time.
 *
 * IR-1228 r5 (Rotem, 2026-09-08 and again 2026-09-09): "last monitoing time - I
 * want the time as well!", then "last monitoring doesnt display the hour".
 * IR-1425 r12b: local getters instead of UTC ones (comment 64191 — the
 * reader's clock, with the zone printed beside it by the caller).
 *
 * Separate from `fmtCreatedDate` rather than a flag on it: the two "Added"
 * columns are genuinely day-resolution facts, and widening them would cost
 * horizontal space in the same round that Rotem is asking for LESS of it.
 * Minutes, not seconds — this column answers "how fresh is this", and a
 * seconds field is three characters of noise at that question's resolution.
 */
export function fmtCreatedDateTime(iso: string | null): string {
  return fmtLocalDateTime(iso);
}

/**
 * IR-1425 r12b: the viewer's local "YYYY-MM-DD HH:MM" for a UTC/offset ISO
 * stamp. Local getters (not `toLocaleString`) so the shape is identical in
 * every browser locale and the tests can compute the same string. Em dash on
 * null / undefined / empty / unparseable.
 */
export function fmtLocalDateTime(iso: string | null | undefined): string {
  const d = parse(iso);
  if (!d) return EM_DASH;
  return `${fmtCreatedDate(iso ?? null)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * IR-1425 r12b: the zone the viewer is reading in, as "UTC", "UTC+03:00" or
 * "UTC-04:30" — one spelling everywhere (not `timeZoneName: "short"`, which
 * prints "GMT+3" in some engines and "IDT" in others: two spellings of one
 * fact on one screen).
 *
 * The offset is taken from `getTimezoneOffset()` OF THE GIVEN INSTANT, not of
 * now, so a January stamp read in July carries the winter offset (DST). With
 * no instant (or an unparseable one) it falls back to the current moment.
 */
export function viewerZoneLabel(iso?: string | null): string {
  const d = parse(iso) ?? new Date();
  const minutes = -d.getTimezoneOffset();
  if (minutes === 0) return "UTC";
  const abs = Math.abs(minutes);
  return `UTC${minutes > 0 ? "+" : "-"}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
}

/** IR-1425 r12b: `fmtLocalDateTime` + " " + `viewerZoneLabel`, or the em dash. */
export function fmtLocalDateTimeWithZone(iso: string | null | undefined): string {
  if (!parse(iso)) return EM_DASH;
  return `${fmtLocalDateTime(iso)} ${viewerZoneLabel(iso)}`;
}

/** True when the group was first seen in IRIS within the last 24h (drives the "NEW" chip). */
export function isNewGroup(iso: string | null, nowMs: number): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return false;
  return nowMs - t <= DAY_MS && nowMs - t >= 0;
}
